'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { exportMonthlyReport } from '@/lib/export-monthly-report'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import { formatTime } from '@/lib/utils'
import { toast } from 'sonner'
import {
  LogOut,
  Menu,
  Home,
  CalendarCheck,
  CalendarDays,
  Plane,
  Home as HomeIcon,
  ClipboardList,
  FolderKanban,
  FileText,
  Bell,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Download,
  Building2,
  Coffee,
  Loader2,
} from 'lucide-react'
import { EmployeeAttendance } from './EmployeeAttendance'
import { TeamCalendar } from '@/components/calendar/TeamCalendar'
import { EmployeeLeave } from './EmployeeLeave'
import { EmployeeWFH } from './EmployeeWFH'
import { EmployeeTasks } from './EmployeeTasks'
import { EmployeeProjects } from './EmployeeProjects'
import { EmployeeDailyWork } from './EmployeeDailyWork'
import { User } from '@/types/user'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { cn } from '@/lib/utils'
import { todayIST } from '@/lib/date-utils'

interface DashboardStats {
  todayAttendance: {
    checkIn: string | null
    checkOut: string | null
    status: string
    workMode: string
    breaks?: { start: string | null; end: string | null }[]
  } | null
  pendingLeaves: number
  pendingWFH: number
  assignedProjects: number
  pendingTasks: number
  completedTasks: number
}

interface Task {
  id: string
  title: string
  status: string
  priority: string
  dueDate: string | null
  project: { name: string } | null
}

interface Notification {
  id: string
  title: string
  message: string
  type: string
  isRead: boolean
  createdAt: string
}

interface EmployeeDashboardProps {
  user: User
  onLogout: () => void
}

const MENU_ITEMS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'attendance', label: 'Attendance', icon: CalendarCheck },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'leave', label: 'Leave', icon: Plane },
  { id: 'wfh', label: 'WFH', icon: HomeIcon },
  { id: 'tasks', label: 'My Tasks', icon: ClipboardList },
  { id: 'daily-work', label: 'Daily Work', icon: FileText },
  { id: 'notifications', label: 'Notifications', icon: Bell },
] as const

export function EmployeeDashboard({ user, onLogout }: EmployeeDashboardProps) {
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [todayTasks, setTodayTasks] = useState<Task[]>([])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [allNotifications, setAllNotifications] = useState<Notification[]>([])
  const [, setLoading] = useState(true)
  
  // New States for Leave Analytics & Quick Actions
  const [leaves, setLeaves] = useState<any[]>([])
  const [busy, setBusy] = useState(false)
  const [workMode, setWorkMode] = useState('OFFICE')
  const [exportMonth, setExportMonth] = useState(() => new Date().toISOString().slice(0, 7))
  const [exporting, setExporting] = useState(false)
  const [workUpdateDone, setWorkUpdateDone] = useState(false)

  // Checkout is gated on today's work update — the server enforces it too, this
  // only keeps the button from promising a click the API will refuse. Refetched
  // on every return to the overview so submitting in the Daily Work tab is picked up.
  const fetchTodayWork = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/daily-work?date=${todayIST()}`)
      const data = await res.json()
      setWorkUpdateDone((data.dailyWorks || []).length > 0)
    } catch (e) {
      console.error("Error fetching today's work update:", e)
    }
  }, [])


  const fetchLeaves = useCallback(async () => {
    try {
      const res = await apiFetch('/api/leave')
      const data = await res.json()
      if (data.leaves) setLeaves(data.leaves)
    } catch (e) {
      console.error('Error fetching leaves:', e)
    }
  }, [])

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiFetch('/api/notifications')
      const data = await res.json()
      if (data.notifications) setAllNotifications(data.notifications)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }, [])

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboard')
      const data = await res.json()
      if (data.stats) setStats(data.stats)
      if (data.todayTasks) setTodayTasks(data.todayTasks)
      if (data.notifications) setNotifications(data.notifications)
    } catch (error) {
      console.error('Error fetching dashboard:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboard()
    fetchLeaves()
  }, [fetchDashboard, fetchLeaves])

  useEffect(() => {
    if (activeTab === 'notifications') fetchNotifications()
  }, [activeTab, fetchNotifications])

  useEffect(() => {
    if (activeTab === 'overview') fetchTodayWork()
  }, [activeTab, fetchTodayWork])

  const activeMeta = MENU_ITEMS.find(m => m.id === activeTab)

  const handleNav = (id: string) => {
    setActiveTab(id)
    setMobileOpen(false)
  }

  const todayStatus = stats?.todayAttendance
    ? stats.todayAttendance.checkOut
      ? 'COMPLETED'
      : stats.todayAttendance.status
    : 'NOT IN'

  // Any break without an end time is the open one. Checking only the *last*
  // entry made the button read "Start Break" whenever an older entry was still
  // open, and the resulting breakstart was rejected as "break already in progress".
  const openBreak = (stats?.todayAttendance?.breaks || []).find(b => b.start && !b.end) || null

  const postAttendanceAction = async (action: string, body: any = {}) => {
    setBusy(true)
    try {
      const res = await apiFetch('/api/attendance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...body }),
      })
      const data = await res.json()

      // Backdated office check-in routed to HR for approval — nothing to record yet.
      if (data.pending) {
        toast.success(data.message || 'Check-in request submitted for approval')
        return
      }

      // Reflect the change in today's snapshot straight from the response, so the
      // Start/End Break (and check-in/out) buttons flip immediately. Previously the
      // card only updated after the heavy /api/dashboard refetch, so when that call
      // was slow or failing (read-quota pressure) "Start Break" looked like it did
      // nothing even though the break was saved.
      setStats(prev => {
        if (!prev) return prev
        const cur = prev.todayAttendance
        if (action === 'breakstart' || action === 'breakend') {
          const base = cur ?? { checkIn: null, checkOut: null, status: 'PRESENT', workMode: 'OFFICE' }
          return { ...prev, todayAttendance: { ...base, breaks: data.breaks ?? cur?.breaks ?? [] } }
        }
        if (data.record) {
          return {
            ...prev,
            todayAttendance: {
              checkIn: data.record.checkIn ?? null,
              checkOut: data.record.checkOut ?? null,
              status: data.record.status ?? 'PRESENT',
              workMode: data.record.workMode ?? 'OFFICE',
              breaks: data.record.breaks ?? [],
            },
          }
        }
        return prev
      })

      toast.success(
        action === 'checkin' ? 'Successfully checked in'
          : action === 'checkout' ? 'Successfully checked out'
          : action === 'breakstart' ? 'Break started'
          : action === 'breakend' ? 'Break ended'
          : 'Action recorded successfully'
      )
      fetchDashboard()
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setBusy(false)
    }
  }

  // Calculate Leave Stats
  const currentYear = new Date().getFullYear()
  const totalApprovedLeaveDays = leaves.filter((l: any) => {
    if (l.status !== 'APPROVED') return false
    return new Date(l.startDate).getFullYear() === currentYear
  }).reduce((sum: number, l: any) => {
    const start = new Date(l.startDate)
    const end = new Date(l.endDate)
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    return sum + (isNaN(diff) ? 0 : diff)
  }, 0)
  const pendingLeavesCount = leaves.filter((l: any) => l.status === 'PENDING').length

  const handleExportExcel = async () => {
    if (!exportMonth) {
      toast.error('Please select a month')
      return
    }
    setExporting(true)
    try {
      const [year, monthStr] = exportMonth.split('-')
      const daysInMonth = new Date(Number(year), Number(monthStr), 0).getDate()
      const startDate = `${exportMonth}-01`
      const endDate = `${exportMonth}-${String(daysInMonth).padStart(2, '0')}`

      const [attendanceRes, dailyWorkRes, tasksRes] = await Promise.all([
        apiFetch('/api/attendance'),
        apiFetch(`/api/daily-work?startDate=${startDate}&endDate=${endDate}`),
        apiFetch('/api/tasks')
      ])

      const attendanceData = await attendanceRes.json()
      const dailyWorkData = await dailyWorkRes.json()
      const tasksData = await tasksRes.json()

      exportMonthlyReport({
        employeeName: user.name || 'Employee',
        employeeId: user.employeeId || 'N/A',
        employeeEmail: user.email || 'N/A',
        department: user.department || 'N/A',
        month: exportMonth,
        attendance: attendanceData.attendance || [],
        dailyWork: dailyWorkData.dailyWorks || [],
        tasks: tasksData.tasks || []
      })
      toast.success('Monthly report exported successfully')
    } catch (error) {
      console.error('Error exporting report:', error)
      toast.error('Failed to export monthly report')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="min-h-screen text-neutral-800 dark:text-white/80">
      {/* Mobile overlay */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-black text-white transition-all duration-300',
            'md:static md:translate-x-0',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
            collapsed ? 'md:w-[76px]' : 'md:w-64',
            'w-72'
          )}
        >
          {/* px-3 (not px-4): the collapsed rail is 76px, so the padding is what
              caps how large the logo box can grow without clipping. */}
          <div className="flex items-center gap-3 px-3 py-4 border-b border-white/10">
            <div className="shrink-0 rounded-xl bg-white p-1 shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg">
                <img src="/sasha-logo-small.png" alt="Sasha Infinity" className="h-8 w-auto" />
              </div>
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="text-sm font-semibold tracking-tight text-white">Sasha Infinity</h1>
                <p className="text-[11px] text-white/40">My Workspace</p>
              </div>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {MENU_ITEMS.map(item => {
              const active = activeTab === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={cn(
                    'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50',
                    active
                      ? 'bg-gradient-to-r from-orange-500/25 to-orange-500/5 text-orange-200 ring-1 ring-inset ring-orange-500/30'
                      : 'text-white/70 hover:text-white hover:bg-white/10',
                    collapsed && 'md:justify-center md:px-2'
                  )}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className={cn('h-4.5 w-4.5 shrink-0', active && 'text-orange-300')} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {active && !collapsed && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-orange-400 shadow-[0_0_8px_2px_rgba(251,146,60,0.6)]" />
                  )}
                </button>
              )
            })}
          </nav>

          {/* Profile footer */}
          <div className="mt-auto border-t border-white/10 p-3">
            {collapsed ? (
              <div className="hidden md:flex flex-col items-center gap-2">
                <Avatar className="h-9 w-9 bg-gradient-to-br from-orange-500 to-orange-600" title={user?.name}>
                  <AvatarFallback className="bg-transparent text-white text-sm font-semibold">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                {/* Settings gear removed: employees have no settings tab, so the
                    button rendered and hovered but had no onClick — dead UI. */}
                <button onClick={onLogout} title="Logout" aria-label="Logout" className="rounded-lg p-2 text-white/60 hover:text-rose-300 hover:bg-white/10 transition-colors">
                  <LogOut className="h-4 w-4" />
                </button>
                <button onClick={() => setCollapsed(false)} title="Expand" aria-label="Expand sidebar" className="rounded-lg p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5 ring-1 ring-inset ring-white/10">
                  <Avatar className="h-9 w-9 shrink-0 bg-gradient-to-br from-orange-500 to-orange-600">
                    <AvatarFallback className="bg-transparent text-white text-sm font-semibold">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                    <p className="text-[11px] text-white/50 truncate">{user.role === 'ADMIN' ? 'Administrator' : user.position || 'Employee'}</p>
                  </div>
                </div>
                <button
                  onClick={onLogout}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
                <button
                  onClick={() => setCollapsed(true)}
                  className="hidden md:flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <ChevronsLeft className="h-4 w-4" /> Collapse
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="sticky top-0 z-20 border-b border-neutral-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 px-3 md:px-6 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-neutral-600 dark:text-white/60 hover:text-white hover:bg-neutral-50 dark:bg-transparent dark:hover:bg-white/5"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <div className="min-w-0">
                  <p className="text-[11px] md:text-xs text-neutral-400 dark:text-white/40 truncate">
                    Welcome back, {user?.name || 'User'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 md:gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative text-neutral-600 dark:text-white/60 hover:text-white hover:bg-neutral-50 dark:bg-transparent dark:hover:bg-white/5">
                      <Bell className="h-4.5 w-4.5" />
                      {notifications.length > 0 && (
                        <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-orange-500 text-[10px] flex items-center justify-center text-white font-medium">
                          {notifications.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 sm:w-80 bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10">
                    <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-neutral-400 dark:text-white/40 text-sm">No new notifications</div>
                    ) : (
                      notifications.slice(0, 5).map((notif) => (
                        <DropdownMenuItem key={notif.id}>
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-sm">{notif.title}</span>
                            <span className="text-xs text-neutral-500 dark:text-white/50">{notif.message}</span>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-auto p-3 md:p-6">
            {activeTab === 'overview' && (
              <PageShell>
                <SectionHeader
                  icon={Home}
                  title={`Hey, ${user?.name?.split(' ')[0] || 'there'} 👋`}
                  description="Here's a snapshot of your day"
                />

                {/* KPI Row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <KpiTile
                    label="Today Status"
                    value={todayStatus}
                    icon={CalendarCheck}
                    tone={stats?.todayAttendance ? 'green' : 'amber'}
                    hint={stats?.todayAttendance ? `In ${formatTime(stats.todayAttendance.checkIn)}` : 'Not checked in'}
                  />
                  <KpiTile
                    label="Open Tasks"
                    value={stats?.pendingTasks ?? 0}
                    icon={ClipboardList}
                    tone="orange"
                    hint={`${stats?.completedTasks ?? 0} done this month`}
                  />
                  <KpiTile
                    label="Requests Pending"
                    value={(stats?.pendingLeaves ?? 0) + (stats?.pendingWFH ?? 0)}
                    icon={Plane}
                    tone="violet"
                    hint={`${stats?.pendingLeaves ?? 0} leave · ${stats?.pendingWFH ?? 0} WFH`}
                  />
                </div>

                {/* Today snapshot + tasks */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
                  <GlassCard tone="orange" className="lg:col-span-1">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-orange-300" />
                      <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">Mark Attendance</h3>
                    </div>
                    {stats?.todayAttendance ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusPill tone={statusTone(stats.todayAttendance.status)}>{stats.todayAttendance.status}</StatusPill>
                          <StatusPill tone="blue">{stats.todayAttendance.workMode}</StatusPill>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-xl bg-neutral-50 dark:bg-white/[0.03] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 p-2 text-center">
                            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">In</p>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-white/90 tabular-nums mt-0.5">{formatTime(stats.todayAttendance.checkIn) || '—'}</p>
                          </div>
                          <div className="rounded-xl bg-neutral-50 dark:bg-white/[0.03] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 p-2 text-center">
                            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Out</p>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-white/90 tabular-nums mt-0.5">{formatTime(stats.todayAttendance.checkOut) || '—'}</p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 mt-2">
                          {!stats.todayAttendance.checkOut && (
                            <>
                              {openBreak ? (
                                <Button
                                  onClick={() => postAttendanceAction('breakend')}
                                  disabled={busy}
                                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg text-xs"
                                >
                                  {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Coffee className="mr-2 h-3.5 w-3.5" />}
                                  End Break
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => postAttendanceAction('breakstart')}
                                  disabled={busy}
                                  className="w-full bg-amber-600 hover:bg-amber-500 text-white shadow-lg text-xs"
                                >
                                  {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Coffee className="mr-2 h-3.5 w-3.5" />}
                                  Start Break
                                </Button>
                              )}

                              <div className="space-y-1">
                                {/* The API refuses a check-out while a break is open,
                                    so surface that here instead of letting the click
                                    bounce off a 400. */}
                                <Button
                                  onClick={() => postAttendanceAction('checkout')}
                                  disabled={busy || !!openBreak || !workUpdateDone}
                                  className="w-full bg-red-600 hover:bg-red-500 text-white shadow-lg text-xs"
                                >
                                  Check Out
                                </Button>
                                {!workUpdateDone && (
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-[10px] text-amber-600 dark:text-amber-400">
                                      Submit your Today&apos;s Work Update before checking out.
                                    </p>
                                    <button
                                      onClick={() => setActiveTab('daily-work')}
                                      className="shrink-0 rounded-md text-[10px] font-semibold text-orange-600 underline-offset-2 hover:underline dark:text-orange-300"
                                    >
                                      Log work
                                    </button>
                                  </div>
                                )}
                                {openBreak && (
                                  <p className="text-[10px] text-amber-600 dark:text-amber-400">
                                    End your break before checking out.
                                  </p>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Work Mode</label>
                          <Select value={workMode} onValueChange={setWorkMode}>
                            <SelectTrigger className="w-full h-8 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-white/10">
                              <SelectValue placeholder="Mode" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="OFFICE">Office</SelectItem>
                              <SelectItem value="WFH">WFH</SelectItem>
                              <SelectItem value="FIELD">Field</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={() => postAttendanceAction('checkin', { workMode })}
                          disabled={busy}
                          className="w-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white shadow-lg shadow-orange-500/20 text-xs"
                        >
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check In'}
                        </Button>
                      </div>
                    )}
                  </GlassCard>

                  <GlassCard className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">Today&apos;s tasks</h3>
                        <p className="text-[11px] text-neutral-400 dark:text-white/40">Your pending and in-progress work</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveTab('tasks')}
                        className="text-xs text-neutral-600 dark:text-white/60 hover:text-white hover:bg-neutral-50 dark:bg-transparent dark:hover:bg-white/5"
                      >
                        View all
                      </Button>
                    </div>
                    {todayTasks.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-neutral-400 dark:text-white/40 gap-2 text-sm">
                        <CheckCircle2 className="h-8 w-8 opacity-30" />
                        All clear for today
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {todayTasks.slice(0, 5).map(task => (
                          <div
                            key={task.id}
                            className="rounded-xl p-3 bg-neutral-50/50 dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 hover:ring-neutral-300 dark:ring-white/15 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-neutral-900 dark:text-white/90 line-clamp-1">{task.title}</p>
                                <p className="text-[11px] text-neutral-400 dark:text-white/40 truncate mt-0.5">{task.project?.name || 'No project'}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <StatusPill tone={task.priority === 'CRITICAL' || task.priority === 'URGENT' ? 'red' : task.priority === 'HIGH' ? 'orange' : 'amber'}>
                                  {task.priority}
                                </StatusPill>
                                {task.dueDate && (
                                  <span className="text-[10px] text-neutral-400 dark:text-white/40 tabular-nums">
                                    {(() => { const d = new Date(task.dueDate); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` })()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </GlassCard>
                </div>

                {/* Quick actions */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <QuickAction icon={CalendarCheck} label="Mark Attendance" hint="Check in / out" tone="orange" onClick={() => setActiveTab('attendance')} />
                  <QuickAction icon={FileText} label="Log Daily Work" hint="Today's notes" tone="blue" onClick={() => setActiveTab('daily-work')} />
                  <QuickAction icon={Plane} label="Request Leave" hint="Apply for leave" tone="violet" onClick={() => setActiveTab('leave')} />
                </div>

                {/* Leave Analytics and Report Exporter Blocks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-3">
                  {/* Leave Analytics Block */}
                  <GlassCard tone="violet">
                    <div className="flex items-center gap-2 mb-3">
                      <Plane className="h-4 w-4 text-violet-300" />
                      <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">Leave Analytics</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl bg-neutral-50 dark:bg-white/[0.03] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 p-3">
                        <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Total Leaves (Approved)</p>
                        <p className="text-lg font-bold text-neutral-900 dark:text-white/90 mt-1">{totalApprovedLeaveDays} days</p>
                      </div>
                      <div className="rounded-xl bg-neutral-50 dark:bg-white/[0.03] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 p-3">
                        <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Pending Request count</p>
                        <p className="text-lg font-bold text-neutral-900 dark:text-white/90 mt-1">{pendingLeavesCount}</p>
                      </div>
                    </div>
                  </GlassCard>

                  {/* Report Exporter */}
                  <GlassCard tone="blue">
                    <div className="flex items-center gap-2 mb-3">
                      <Download className="h-4 w-4 text-blue-300" />
                      <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">Report Exporter</h3>
                    </div>
                    <div className="flex flex-col justify-between h-full min-h-[92px] space-y-3">
                      <p className="text-xs text-neutral-400 dark:text-white/50">
                        Export your monthly attendance, project logs, and task completion records to Word.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="month"
                          value={exportMonth}
                          onChange={(e) => setExportMonth(e.target.value)}
                          className="text-xs h-9 bg-white/5 border-white/10 text-white dark:bg-white/[0.02]"
                        />
                        <Button
                          disabled={exporting}
                          onClick={handleExportExcel}
                          className="bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white shadow-lg text-xs h-9 px-4 shrink-0 font-semibold"
                        >
                          {exporting ? 'Exporting...' : 'Export to Word'}
                        </Button>
                      </div>
                    </div>
                  </GlassCard>
                </div>
              </PageShell>
            )}

            {activeTab === 'attendance' && (
              <PageShell>
                <EmployeeAttendance onRefresh={fetchDashboard} userId={user.id} onNavigate={handleNav} />
              </PageShell>
            )}
            {activeTab === 'calendar' && (
              <PageShell>
                <TeamCalendar userId={user.id} />
              </PageShell>
            )}
            {activeTab === 'leave' && (
              <PageShell>
                <EmployeeLeave />
              </PageShell>
            )}
            {activeTab === 'wfh' && (
              <PageShell>
                <EmployeeWFH />
              </PageShell>
            )}
            {activeTab === 'tasks' && (
              <PageShell>
                <EmployeeTasks onRefresh={fetchDashboard} />
              </PageShell>
            )}
            {activeTab === 'daily-work' && (
              <PageShell>
                <EmployeeDailyWork userId={user.id} />
              </PageShell>
            )}
            {activeTab === 'notifications' && (
              <PageShell>
                <SectionHeader
                  icon={Bell}
                  title="Notifications"
                  description="Updates and alerts for you"
                />
                <GlassCard>
                  {allNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-neutral-400 dark:text-white/40 gap-2 text-sm">
                      <Sparkles className="h-10 w-10 opacity-30" />
                      You&apos;re all caught up
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {allNotifications.map(notif => (
                        <div
                          key={notif.id}
                          className="rounded-xl p-3.5 bg-neutral-50/50 dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 hover:ring-neutral-300 dark:ring-white/15 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1 space-y-1">
                              <p className="text-sm font-medium text-neutral-900 dark:text-white/90">{notif.title}</p>
                              <p className="text-sm text-neutral-600 dark:text-white/60">{notif.message}</p>
                              <p className="text-[11px] text-neutral-400 dark:text-white/40">
                                {(() => { const d = new Date(notif.createdAt); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}` })()}
                              </p>
                            </div>
                            <StatusPill tone={
                              notif.type.includes('APPROVED') ? 'green' :
                              notif.type.includes('REJECTED') ? 'red' :
                              'blue'
                            }>
                              {notif.type.includes('APPROVED') ? 'Approved' : notif.type.includes('REJECTED') ? 'Rejected' : 'Info'}
                            </StatusPill>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </GlassCard>
              </PageShell>
            )}
          </main>
        </div>
      </div>
    </div>
  )
}

function QuickAction({
  icon: Icon,
  label,
  hint,
  tone,
  onClick,
}: {
  icon: React.ElementType
  label: string
  hint: string
  tone: 'orange' | 'blue' | 'violet'
  onClick: () => void
}) {
  const toneClass: Record<string, string> = {
    orange: 'text-orange-300 bg-orange-500/10',
    blue: 'text-blue-300 bg-blue-500/10',
    violet: 'text-violet-300 bg-violet-500/10',
  }
  return (
    <GlassCard tone={tone} interactive>
      <button onClick={onClick} className="flex items-center gap-3 w-full text-left">
        <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', toneClass[tone])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-900 dark:text-white/90 truncate">{label}</p>
          <p className="text-xs text-neutral-500 dark:text-white/50 truncate">{hint}</p>
        </div>
      </button>
    </GlassCard>
  )
}
